import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '../../design-system/components/WebAppShell';
import { reviewDesk } from '../../design-system/theme/reviewDesk';
import { useTranslation } from '../../hooks/useTranslation';
import MentorTrainingAPI from '../../services/api/mentorTrainingAPI';
import {
  AttemptResponse,
  AttemptSubmitRequest,
  COMPETENCY_CODES,
  COMPETENCY_LABELS,
  CompetencyCode,
  DrillDetail,
  DrillLevel,
  LEVEL_LABELS,
  StudentCompetencyGrading,
  VALID_LEVELS,
} from '../../types/mentorTraining';

const LEVEL_OPTIONS: DrillLevel[] = ['below_acc', 'acc', 'ready_for_pcc', 'pcc', 'mcc'];

function blankCompetencyGrading(): Record<CompetencyCode, StudentCompetencyGrading> {
  return COMPETENCY_CODES.reduce(
    (acc, code) => {
      acc[code] = { level: 'acc', evidence_quote: '' };
      return acc;
    },
    {} as Record<CompetencyCode, StudentCompetencyGrading>
  );
}

function levelDisplay(level: DrillLevel | null | undefined, lang: 'en' | 'tr'): string {
  if (!level) return '—';
  return LEVEL_LABELS[level]?.[lang] ?? level;
}

function scoreColor(score: number): string {
  if (score >= 80) return reviewDesk.success;
  if (score >= 50) return reviewDesk.amber;
  return '#C44C3F';
}

function ResultsPanel({
  attempt,
  drill,
  lang,
  t,
}: {
  attempt: AttemptResponse;
  drill: DrillDetail;
  lang: 'en' | 'tr';
  t: (k: string, fb?: string) => string;
}) {
  const agreement = attempt.agreement_result;
  const critique = attempt.feedback_critique;
  if (!agreement) {
    return (
      <View style={styles.resultsBox}>
        <Text style={styles.resultsTitle}>
          {t('mentorTraining.results.processing')}
        </Text>
      </View>
    );
  }
  return (
    <View style={styles.resultsBox}>
      <View style={styles.resultsHeader}>
        <View style={styles.resultsScoreCircle}>
          <Text style={styles.resultsScoreNumber}>{agreement.score_percent}</Text>
          <Text style={styles.resultsScoreUnit}>%</Text>
        </View>
        <View style={styles.resultsHeaderText}>
          <Text style={styles.resultsTitle}>
            {t('mentorTraining.results.title')}
          </Text>
          <Text style={styles.resultsBody}>
            {t('mentorTraining.results.summary')}
            {' · '}
            {agreement.exact_match_count}/{agreement.total_competencies}{' '}
            {t('mentorTraining.results.exactMatches')}
          </Text>
        </View>
      </View>

      <View style={styles.compRows}>
        {agreement.per_competency.map((row) => {
          const label = COMPETENCY_LABELS[row.code]?.[lang] ?? row.code;
          return (
            <View key={row.code} style={styles.compRow}>
              <View style={styles.compRowHead}>
                <Text style={styles.compCode}>{row.code}</Text>
                <Text style={styles.compName}>{label}</Text>
                <View style={[styles.compScorePill, { backgroundColor: scoreColor(row.score) + '22' }]}>
                  <Text style={[styles.compScorePillText, { color: scoreColor(row.score) }]}>
                    {row.score}
                  </Text>
                </View>
              </View>
              <View style={styles.compLevels}>
                <View style={styles.compLevelBox}>
                  <Text style={styles.compLevelLabel}>{t('mentorTraining.results.you')}</Text>
                  <Text style={styles.compLevelValue}>{levelDisplay(row.student_level, lang)}</Text>
                </View>
                <MaterialIcons name="east" size={16} color={reviewDesk.muted} />
                <View style={styles.compLevelBox}>
                  <Text style={styles.compLevelLabel}>{t('mentorTraining.results.mcai')}</Text>
                  <Text style={[styles.compLevelValue, { color: reviewDesk.accent }]}>
                    {levelDisplay(row.ground_truth_level, lang)}
                  </Text>
                </View>
              </View>
              {row.ground_truth_evidence.length > 0 && (
                <View style={styles.evidenceBox}>
                  <Text style={styles.evidenceLabel}>
                    {t('mentorTraining.results.evidence')}:
                  </Text>
                  {row.ground_truth_evidence.slice(0, 2).map((q, i) => (
                    <Text key={i} style={styles.evidenceQuote}>“{q}”</Text>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </View>

      {critique && (
        <View style={styles.critiqueBox}>
          <Text style={styles.critiqueTitle}>
            {t('mentorTraining.results.critiqueTitle')}
          </Text>
          <View style={styles.critiqueAlignment}>
            <Text style={styles.critiqueAlignLabel}>
              {t('mentorTraining.results.alignment')}:
            </Text>
            <Text style={[styles.critiqueAlignValue, { color: scoreColor(critique.alignment_score) }]}>
              {critique.alignment_score}/100
            </Text>
          </View>
          <View style={styles.critiquePart}>
            <Text style={styles.critiquePartTitle}>
              {t('mentorTraining.results.strengthCritique')} · {critique.strengths_quality.score}/10
            </Text>
            <Text style={styles.critiqueReview}>{critique.strengths_quality.review}</Text>
            {!!critique.strengths_quality.suggested_rewrite && (
              <View style={styles.rewriteBox}>
                <Text style={styles.rewriteLabel}>
                  {t('mentorTraining.results.suggestedRewrite')}:
                </Text>
                <Text style={styles.rewriteText}>{critique.strengths_quality.suggested_rewrite}</Text>
              </View>
            )}
          </View>
          <View style={styles.critiquePart}>
            <Text style={styles.critiquePartTitle}>
              {t('mentorTraining.results.growthCritique')} · {critique.growth_quality.score}/10
            </Text>
            <Text style={styles.critiqueReview}>{critique.growth_quality.review}</Text>
            {!!critique.growth_quality.suggested_rewrite && (
              <View style={styles.rewriteBox}>
                <Text style={styles.rewriteLabel}>
                  {t('mentorTraining.results.suggestedRewrite')}:
                </Text>
                <Text style={styles.rewriteText}>{critique.growth_quality.suggested_rewrite}</Text>
              </View>
            )}
          </View>
          {critique.tips.length > 0 && (
            <View style={styles.tipsBox}>
              <Text style={styles.tipsTitle}>
                {t('mentorTraining.results.tips')}:
              </Text>
              {critique.tips.map((tip, i) => (
                <View key={i} style={styles.tipRow}>
                  <Text style={styles.tipBullet}>•</Text>
                  <Text style={styles.tipText}>{tip}</Text>
                </View>
              ))}
            </View>
          )}
          {critique._fallback && (
            <Text style={styles.fallbackNote}>
              {t(
                'mentorTraining.results.fallback'
              )}
            </Text>
          )}
        </View>
      )}
    </View>
  );
}

export default function DrillScreen() {
  const router = useRouter();
  const { t, language } = useTranslation();
  const lang: 'en' | 'tr' = language === 'tr' ? 'tr' : 'en';
  const params = useLocalSearchParams<{ drillId: string }>();
  const drillId = Number(params.drillId);

  const [drill, setDrill] = useState<DrillDetail | null>(null);
  const [attempt, setAttempt] = useState<AttemptResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [competencies, setCompetencies] = useState<Record<CompetencyCode, StudentCompetencyGrading>>(
    blankCompetencyGrading()
  );
  const [overallLevel, setOverallLevel] = useState<DrillLevel>('acc');
  const [strength, setStrength] = useState('');
  const [growth, setGrowth] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(drillId)) {
      setError('Invalid drill id');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [d, a] = await Promise.all([
        MentorTrainingAPI.getMyDrill(drillId),
        MentorTrainingAPI.getMyAttempt(drillId),
      ]);
      setDrill(d);
      setAttempt(a);
      // Mark started if not yet started.
      if (!a) {
        try {
          await MentorTrainingAPI.startMyDrill(drillId);
        } catch (_e) {
          // best-effort
        }
      }
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [drillId]);

  useEffect(() => {
    load();
  }, [load]);

  const isSubmitted = !!attempt?.submitted_at;

  const handleSubmit = useCallback(async () => {
    setValidationError(null);
    for (const code of COMPETENCY_CODES) {
      const entry = competencies[code];
      if (!entry || !VALID_LEVELS.includes(entry.level)) {
        setValidationError(
          t('mentorTraining.validation.allCompetencies')
        );
        return;
      }
    }
    if (!strength.trim() || !growth.trim()) {
      setValidationError(
        t(
          'mentorTraining.validation.feedbackRequired'
        )
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload: AttemptSubmitRequest = {
        competencies,
        overall_level: overallLevel,
        written_feedback: { strength: strength.trim(), growth: growth.trim() },
        language: lang,
      };
      const updated = await MentorTrainingAPI.submitMyDrill(drillId, payload);
      setAttempt(updated);
    } catch (e: any) {
      setValidationError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [competencies, overallLevel, strength, growth, drillId, lang, t]);

  const setCompetency = useCallback(
    (code: CompetencyCode, patch: Partial<StudentCompetencyGrading>) => {
      setCompetencies((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
    },
    []
  );

  const headerRight = useMemo(() => {
    if (!drill) return null;
    return (
      <View style={styles.topRightRow}>
        <TouchableOpacity
          onPress={() => router.push('/mentor-training' as any)}
          style={styles.backBtn}
        >
          <MaterialIcons name="arrow-back" size={16} color={reviewDesk.inkSoft} />
          <Text style={styles.backBtnText}>
            {t('mentorTraining.back')}
          </Text>
        </TouchableOpacity>
        <View style={styles.topLevelPill}>
          <Text style={styles.topLevelText}>{drill.target_level.toUpperCase()}</Text>
        </View>
      </View>
    );
  }, [drill, router, t]);

  if (loading) {
    return (
      <WebAppShell active="mentor-training">
        <View style={styles.loaderBox}>
          <ActivityIndicator size="large" color={reviewDesk.accent} />
        </View>
      </WebAppShell>
    );
  }
  if (error || !drill) {
    return (
      <WebAppShell active="mentor-training">
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error || 'Drill not found'}</Text>
          <TouchableOpacity onPress={() => router.push('/mentor-training' as any)} style={styles.primaryBtn}>
            <Text style={styles.primaryBtnText}>{t('mentorTraining.back')}</Text>
          </TouchableOpacity>
        </View>
      </WebAppShell>
    );
  }

  return (
    <WebAppShell active="mentor-training" topRight={headerRight}>
      <View style={styles.container}>
        <View style={styles.titleBlock}>
          {!!drill.module_name && <Text style={styles.moduleLabel}>{drill.module_name}</Text>}
          <Text style={styles.drillTitle}>{drill.title}</Text>
          {!!drill.instructions && (
            <View style={styles.instructionsBox}>
              <MaterialIcons name="lightbulb-outline" size={15} color={reviewDesk.amber} />
              <Text style={styles.instructionsText}>{drill.instructions}</Text>
            </View>
          )}
        </View>

        <View style={styles.splitPane}>
          <View style={styles.transcriptColumn}>
            <View style={styles.columnHeader}>
              <Text style={styles.columnTitle}>
                {t('mentorTraining.transcript')}
              </Text>
            </View>
            <ScrollView style={styles.transcriptScroller}>
              {drill.transcript_excerpt ? (
                <Text style={styles.transcriptText}>{drill.transcript_excerpt}</Text>
              ) : (
                <View style={styles.transcriptMissing}>
                  <Text style={styles.transcriptMissingText}>
                    {t(
                      'mentorTraining.noTranscript'
                    )}
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>

          <View style={styles.gradingColumn}>
            <View style={styles.columnHeader}>
              <Text style={styles.columnTitle}>
                {isSubmitted
                  ? t('mentorTraining.yourSubmission')
                  : t('mentorTraining.gradeAsMentor')}
              </Text>
            </View>
            <ScrollView style={styles.gradingScroller}>
              {isSubmitted && attempt ? (
                <ResultsPanel attempt={attempt} drill={drill} lang={lang} t={t} />
              ) : (
                <View style={styles.gradingForm}>
                  {COMPETENCY_CODES.map((code) => {
                    const entry = competencies[code];
                    const label = COMPETENCY_LABELS[code]?.[lang] ?? code;
                    return (
                      <View key={code} style={styles.compFieldset}>
                        <View style={styles.compFieldsetHead}>
                          <Text style={styles.compFieldsetCode}>{code}</Text>
                          <Text style={styles.compFieldsetLabel}>{label}</Text>
                        </View>
                        <View style={styles.levelOptionsRow}>
                          {LEVEL_OPTIONS.map((opt) => {
                            const active = entry.level === opt;
                            return (
                              <TouchableOpacity
                                key={opt}
                                onPress={() => setCompetency(code, { level: opt })}
                                style={[styles.levelOption, active && styles.levelOptionActive]}
                              >
                                <Text style={[styles.levelOptionText, active && styles.levelOptionTextActive]}>
                                  {LEVEL_LABELS[opt]?.[lang] ?? opt}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                        <TextInput
                          style={styles.evidenceInput}
                          value={entry.evidence_quote}
                          onChangeText={(text) => setCompetency(code, { evidence_quote: text })}
                          placeholder={t(
                            'mentorTraining.evidencePlaceholder'
                          )}
                          placeholderTextColor={reviewDesk.muted}
                          multiline
                          numberOfLines={2}
                        />
                      </View>
                    );
                  })}

                  <View style={styles.overallBlock}>
                    <Text style={styles.overallLabel}>
                      {t('mentorTraining.overallLevel')}
                    </Text>
                    <View style={styles.levelOptionsRow}>
                      {LEVEL_OPTIONS.map((opt) => {
                        const active = overallLevel === opt;
                        return (
                          <TouchableOpacity
                            key={opt}
                            onPress={() => setOverallLevel(opt)}
                            style={[styles.levelOption, active && styles.levelOptionActive]}
                          >
                            <Text style={[styles.levelOptionText, active && styles.levelOptionTextActive]}>
                              {LEVEL_LABELS[opt]?.[lang] ?? opt}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>

                  <View style={styles.feedbackBlock}>
                    <Text style={styles.feedbackBlockTitle}>
                      {t('mentorTraining.writtenFeedback')}
                    </Text>
                    <Text style={styles.feedbackHint}>
                      {t(
                        'mentorTraining.writtenHint'
                      )}
                    </Text>
                    <Text style={styles.feedbackLabel}>
                      {t('mentorTraining.strength')}
                    </Text>
                    <TextInput
                      style={styles.feedbackInput}
                      value={strength}
                      onChangeText={setStrength}
                      placeholder={t('mentorTraining.strengthPlaceholder')}
                      placeholderTextColor={reviewDesk.muted}
                      multiline
                    />
                    <Text style={styles.feedbackLabel}>
                      {t('mentorTraining.growth')}
                    </Text>
                    <TextInput
                      style={styles.feedbackInput}
                      value={growth}
                      onChangeText={setGrowth}
                      placeholder={t(
                        'mentorTraining.growthPlaceholder'
                      )}
                      placeholderTextColor={reviewDesk.muted}
                      multiline
                    />
                  </View>

                  {!!validationError && (
                    <View style={styles.validationBox}>
                      <Text style={styles.validationText}>{validationError}</Text>
                    </View>
                  )}

                  <Pressable
                    onPress={handleSubmit}
                    disabled={submitting}
                    style={({ hovered, pressed }: any) => [
                      styles.submitBtn,
                      submitting && { opacity: 0.5 },
                      hovered && !submitting && { backgroundColor: '#1F36A0' },
                      pressed && { opacity: 0.85 },
                    ]}
                  >
                    {submitting ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={styles.submitBtnText}>
                        {t('mentorTraining.submit')}
                      </Text>
                    )}
                  </Pressable>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </View>
    </WebAppShell>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingVertical: 18,
    gap: 16,
    flex: 1,
  },
  topRightRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  backBtnText: {
    fontSize: 13,
    color: reviewDesk.inkSoft,
  },
  topLevelPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: reviewDesk.accent,
  },
  topLevelText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
  },
  loaderBox: { padding: 60, alignItems: 'center' },
  errorBox: {
    padding: 30,
    backgroundColor: '#FFE7E1',
    borderRadius: 10,
    margin: 20,
    gap: 12,
    alignItems: 'flex-start',
  },
  errorText: { color: '#7A2B1F' },
  primaryBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: reviewDesk.accent,
    borderRadius: 8,
  },
  primaryBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  titleBlock: { gap: 6 },
  moduleLabel: {
    fontSize: 12,
    color: reviewDesk.muted,
    fontWeight: '600',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  drillTitle: { fontSize: 22, fontWeight: '700', color: reviewDesk.ink },
  instructionsBox: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    backgroundColor: reviewDesk.amberSoft,
    borderRadius: 8,
    alignItems: 'flex-start',
    marginTop: 6,
  },
  instructionsText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 19,
    color: reviewDesk.inkSoft,
  },
  splitPane: {
    flexDirection: 'row',
    gap: 16,
    flex: 1,
    minHeight: 600,
  },
  transcriptColumn: {
    flex: 1,
    backgroundColor: reviewDesk.card,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  gradingColumn: {
    flex: 1,
    backgroundColor: reviewDesk.card,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  columnHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: reviewDesk.cardMuted,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
  },
  columnTitle: { fontSize: 14, fontWeight: '600', color: reviewDesk.ink },
  transcriptScroller: { padding: 18, flex: 1 },
  transcriptText: {
    fontSize: 14,
    lineHeight: 22,
    color: reviewDesk.ink,
    fontFamily: 'monospace' as any,
  },
  transcriptMissing: {
    padding: 30,
    alignItems: 'center',
  },
  transcriptMissingText: {
    color: reviewDesk.muted,
    textAlign: 'center',
    fontSize: 13,
  },
  gradingScroller: { flex: 1 },
  gradingForm: { padding: 16, gap: 14 },
  compFieldset: {
    paddingBottom: 12,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
    gap: 8,
  },
  compFieldsetHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  compFieldsetCode: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    backgroundColor: reviewDesk.accentSoft,
    color: reviewDesk.accent,
    borderRadius: 4,
    fontSize: 11,
    fontWeight: '700',
  },
  compFieldsetLabel: { fontSize: 13, fontWeight: '600', color: reviewDesk.ink, flexShrink: 1 },
  levelOptionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  levelOption: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
  },
  levelOptionActive: {
    backgroundColor: reviewDesk.accent,
    borderColor: reviewDesk.accent,
  },
  levelOptionText: { fontSize: 12, color: reviewDesk.inkSoft, fontWeight: '500' },
  levelOptionTextActive: { color: '#fff', fontWeight: '600' },
  evidenceInput: {
    minHeight: 50,
    padding: 10,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: reviewDesk.ink,
    textAlignVertical: 'top',
  },
  overallBlock: {
    paddingTop: 14,
    paddingBottom: 12,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
    gap: 8,
  },
  overallLabel: { fontSize: 14, fontWeight: '600', color: reviewDesk.ink },
  feedbackBlock: { gap: 8, paddingTop: 6 },
  feedbackBlockTitle: { fontSize: 14, fontWeight: '600', color: reviewDesk.ink },
  feedbackHint: { fontSize: 12, color: reviewDesk.muted, marginBottom: 4 },
  feedbackLabel: { fontSize: 12, fontWeight: '600', color: reviewDesk.inkSoft, marginTop: 4 },
  feedbackInput: {
    minHeight: 70,
    padding: 12,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 8,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    lineHeight: 19,
    color: reviewDesk.ink,
    textAlignVertical: 'top',
  },
  validationBox: {
    padding: 10,
    backgroundColor: '#FFE7E1',
    borderRadius: 6,
  },
  validationText: { color: '#7A2B1F', fontSize: 12 },
  submitBtn: {
    marginTop: 4,
    paddingVertical: 13,
    paddingHorizontal: 18,
    backgroundColor: reviewDesk.accent,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },

  // results
  resultsBox: { padding: 18, gap: 18 },
  resultsHeader: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  resultsScoreCircle: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  resultsScoreNumber: { color: '#fff', fontSize: 22, fontWeight: '700' },
  resultsScoreUnit: { color: '#fff', fontSize: 11, fontWeight: '500', marginLeft: 1 },
  resultsHeaderText: { flex: 1 },
  resultsTitle: { fontSize: 16, fontWeight: '700', color: reviewDesk.ink },
  resultsBody: { fontSize: 12, color: reviewDesk.muted, marginTop: 3 },
  compRows: { gap: 10 },
  compRow: {
    padding: 12,
    backgroundColor: reviewDesk.cardMuted,
    borderRadius: 8,
    gap: 8,
  },
  compRowHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  compCode: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: reviewDesk.accentSoft,
    color: reviewDesk.accent,
    fontSize: 11,
    fontWeight: '700',
    borderRadius: 4,
  },
  compName: { fontSize: 13, fontWeight: '600', color: reviewDesk.ink, flex: 1 },
  compScorePill: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 5 },
  compScorePillText: { fontSize: 12, fontWeight: '700' },
  compLevels: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  compLevelBox: {
    flex: 1,
    padding: 8,
    backgroundColor: reviewDesk.card,
    borderRadius: 6,
    borderColor: reviewDesk.line,
    borderWidth: 1,
  },
  compLevelLabel: { fontSize: 10, color: reviewDesk.muted, fontWeight: '600', textTransform: 'uppercase' },
  compLevelValue: { fontSize: 13, fontWeight: '600', color: reviewDesk.ink, marginTop: 2 },
  evidenceBox: { gap: 4 },
  evidenceLabel: { fontSize: 11, color: reviewDesk.muted, fontWeight: '600' },
  evidenceQuote: { fontSize: 12, color: reviewDesk.inkSoft, fontStyle: 'italic', lineHeight: 17 },
  critiqueBox: {
    padding: 14,
    backgroundColor: reviewDesk.accentWash,
    borderRadius: 10,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    gap: 10,
  },
  critiqueTitle: { fontSize: 14, fontWeight: '700', color: reviewDesk.ink },
  critiqueAlignment: { flexDirection: 'row', alignItems: 'baseline', gap: 6 },
  critiqueAlignLabel: { fontSize: 12, color: reviewDesk.muted },
  critiqueAlignValue: { fontSize: 14, fontWeight: '700' },
  critiquePart: { gap: 5 },
  critiquePartTitle: { fontSize: 13, fontWeight: '600', color: reviewDesk.ink },
  critiqueReview: { fontSize: 13, color: reviewDesk.inkSoft, lineHeight: 19 },
  rewriteBox: {
    padding: 8,
    backgroundColor: reviewDesk.card,
    borderRadius: 6,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    gap: 3,
  },
  rewriteLabel: { fontSize: 10, color: reviewDesk.muted, fontWeight: '600', textTransform: 'uppercase' },
  rewriteText: { fontSize: 13, color: reviewDesk.ink, lineHeight: 19 },
  tipsBox: { gap: 4 },
  tipsTitle: { fontSize: 12, fontWeight: '600', color: reviewDesk.inkSoft },
  tipRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },
  tipBullet: { color: reviewDesk.accent, fontWeight: '700' },
  tipText: { flex: 1, fontSize: 12, color: reviewDesk.inkSoft, lineHeight: 17 },
  fallbackNote: {
    fontSize: 11,
    color: reviewDesk.muted,
    fontStyle: 'italic',
    marginTop: 4,
  },
});
